import React, { useEffect, useState } from 'react';
import { Loader2, RotateCw, Printer, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export function CampaignHistory({ onPrintLetters }) {
  const { commsCampaigns, commsCampaignFollowUp } = useAuth();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState({});
  const [err, setErr] = useState('');

  const load = () => commsCampaigns().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const followUp = async (c, force) => {
    if (!window.confirm(`Resend "${c.subject}" to ${c.still_unpaid.length} member(s) who still appear unpaid?`)) return;
    setBusy(c.id); setErr('');
    try { const r = await commsCampaignFollowUp(c.id, force); setResult((s) => ({ ...s, [c.id]: r })); load(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Follow-up failed'); }
    setBusy('');
  };

  if (items === null) return <p className="text-xs" style={{ color: 'var(--olive)' }}><Loader2 className="animate-spin inline" size={12} /> Loading campaigns…</p>;
  return (
    <div className="space-y-2" data-testid="campaign-history">
      <h4 className="font-title text-sm" style={{ color: 'var(--navy)' }}>Renewal campaigns</h4>
      {items.length === 0 && <p className="text-xs" style={{ color: 'var(--olive)' }}>No renewal campaigns sent yet — use "Subscriptions due" above.</p>}
      {items.map((c) => {
        const r = result[c.id];
        return (
          <div key={c.id} className="rounded border px-3 py-2 text-xs space-y-1" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid={`campaign-${c.id}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-title">{new Date(c.sent_at).toLocaleDateString()}</span>
              <span>{c.channel === 'letter' ? 'Letters' : 'Email'} · {c.subject}</span>
              {c.follow_up_of && <span className="px-1.5 rounded" style={{ background: 'rgba(0,0,0,0.06)' }}>follow-up</span>}
              <span style={{ color: 'var(--olive)' }}>{c.recipients} sent · <b style={{ color: '#1a7f37' }}>{c.paid_since} paid since</b> · <b style={{ color: c.still_unpaid.length ? '#8a5a00' : 'var(--olive)' }}>{c.still_unpaid.length} still unpaid</b></span>
              {c.still_unpaid.length > 0 && (
                <button data-testid={`campaign-followup-${c.id}`} onClick={() => followUp(c, !c.follow_up_available)} disabled={busy === c.id}
                  title={c.follow_up_available ? 'Resend to everyone still unpaid' : `Follow-ups open in ${c.follow_up_in_days} day(s) (30 days after sending) — click to send early`}
                  className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded font-title" style={{ background: c.follow_up_available ? 'var(--maroon)' : 'white', color: c.follow_up_available ? 'var(--cream)' : 'var(--olive)', border: '1px solid rgba(0,0,0,0.15)' }}>
                  {busy === c.id ? <Loader2 className="animate-spin" size={12} /> : <RotateCw size={12} />} Resend to {c.still_unpaid.length} still unpaid{c.follow_up_available ? '' : ` (in ${c.follow_up_in_days}d)`}
                </button>
              )}
            </div>
            {r && (
              <p className="inline-flex flex-wrap items-center gap-2" data-testid={`campaign-result-${c.id}`}>
                <CheckCircle2 size={12} style={{ color: '#1a7f37' }} /> Reminder emailed to {r.sent}{r.failed?.length ? ` (${r.failed.length} failed)` : ''}.
                {r.no_email?.length > 0 && <>
                  <span style={{ color: '#8a5a00' }}>{r.no_email.length} without email:</span> {r.no_email.map((u) => u.name).join(', ')}
                  <button data-testid={`campaign-print-${c.id}`} onClick={() => onPrintLetters(r.no_email.map((u) => u.user_id), c)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}><Printer size={12} /> Print letters</button>
                </>}
              </p>
            )}
          </div>
        );
      })}
      {err && <p className="text-xs" style={{ color: '#8a1f11' }} data-testid="campaign-error">{err}</p>}
    </div>
  );
}
